import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Local error boundary — catches render-time exceptions from a subtree and
 * shows a friendly recovery card instead of the opaque CRA "Script error."
 * overlay.  Each boundary is scoped to a single tab / feature so an error
 * in one panel doesn't take down the whole page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.name || 'unnamed'}]`, error, info?.componentStack);
  }
  reset = () => this.setState({ hasError: false, error: null });
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="rounded-xl border p-6 flex items-start gap-3"
        style={{ borderColor: 'rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.06)' }}
        data-testid={`error-boundary-${this.props.name || 'unnamed'}`}>
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-red-500"/>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {this.props.title || 'This panel hit an unexpected error'}
          </div>
          <div className="text-xs mt-1 font-mono break-all" style={{ color: 'var(--text-muted)' }}>
            {String(this.state.error?.message || this.state.error || 'Unknown error')}
          </div>
          <div className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
            The rest of the page is still working. Reload the panel to try again — if it keeps happening, hard-refresh the page.
          </div>
          <button onClick={this.reset}
            className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded text-xs font-medium border"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <RefreshCw className="w-3 h-3"/> Reload panel
          </button>
        </div>
      </div>
    );
  }
}
